import { useState } from 'react';
import { debug } from 'zf-web-infra';
import { Table as BaseTable } from 'antd';
import { assert } from 'zf-infra';
import { Icons } from 'zf-web-components/icons';
import { useHeight } from '../../hooks';
import { t } from '../i18n';
import { Action, Column, Day, Mapping, No, Text, Input, Customize } from './column';
import { Empty } from './components/empty';

import styles from './style.module.less';
import type { TableProps } from './types';
import type { AnyObject } from 'antd/es/_util/type';
import type { CSSProperties, Key, ReactElement } from 'react';
import './style.less';

export function Table<T extends AnyObject = AnyObject>(props: TableProps<T>): ReactElement {
  debug.common('Table', props);

  const [highlightKey, setHighlightKey] = useState<Key>('');

  const {
    pageSize = 10,
    total = props.dataSource?.length,
    className = '',
    rowKey = '_id',
    bordered = true,
    current = 1,
    virtual,
    emptyText = <Empty />
  } = props;

  const tableProps: TableProps<T> = {
    ...props,
    className,
    rowKey,
    children: props.children,
    bordered,
    size: props.size,
    locale: { emptyText: props.loading === true ? <></> : emptyText },
    columns: props.columns ?? new Column().childrenToColumn(props),
    virtual,
    pagination: props.pagination ?? {
      current,
      hideOnSinglePage: total == null || total === 0,
      defaultPageSize: pageSize,
      total,
      onChange: props.onPageChange,
      showTotal: (total => t('共 {total} 条', { total }))
    },
    rowClassName: (record): string => {
      let rowKey = record._id;

      if (props.rowHighlight?.key != null) {
        assert(record[props.rowHighlight.key] != null, '字段不存在');
        rowKey = record[props.rowHighlight.key];
      }

      const isHighlight = rowKey != null && highlightKey === rowKey;
      return isHighlight ? 'highlight' : '';
    },
    onRow: record => ({
      onClick: (): void => {
        let rowKey = record._id;

        if (props.rowHighlight?.key != null) {
          assert(record[props.rowHighlight.key] != null, '字段不存在');
          rowKey = record[props.rowHighlight.key];
        }

        setHighlightKey(rowKey);
      }
    })
  };

  // 分页的高度 64
  const height = useHeight('.ant-table-body');
  if (props.dataSource != null && props.dataSource.length > 0) {
    tableProps.scroll = props.mobile === true ? { x: props.scroll?.y ?? 'max-content' } : {
      x: props.scroll?.x,
      y: props.scroll?.y ?? height - (props.pagination === false ? 0 : 64)
    };
  }

  // 使用统一的loading
  const loading = props.loading === true ? {
    spinning: props.loading,
    indicator: <Icons name='LoadingIcon' className={styles.tableLoading} />,
    wrapperClassName: 'table-container'
  } : false;

  const style: CSSProperties = {
    paddingBottom: props.mobile === true ? 128 : 0
  };

  return (
    <BaseTable loading={loading} {...tableProps} style={style}>
      {props.children}
    </BaseTable>
  );
}

Table.No = No;
Table.Day = Day;
Table.Map = Mapping;
Table.Text = Text;
Table.Input = Input;
Table.Action = Action;
Table.Customize = Customize;
Table.Column = BaseTable.Column; // TODO: 删掉
Table.ColumnGroup = BaseTable.ColumnGroup;
Table.Summary = BaseTable.Summary;

export * from './types';
export * from './components/empty';
